import { create } from 'zustand';
import { useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useRef } from 'react';
import { ApiError } from '@/api/errors';
import { qk } from '@/api/queries';
import type { Drop, DropWaitlist } from '@/api/types';
import { useWsEvent } from '@/realtime';
import { toast } from '@/stores/toasts';

interface SeatState {
  /** Drops where a seat just opened for me (WS drop.waitlist). */
  open: Record<string, number>;
  mark: (dropId: string) => void;
  clear: (dropId: string) => void;
}

export const useDropSeats = create<SeatState>((set) => ({
  open: {},
  mark: (id) => set((s) => ({ open: { ...s.open, [id]: Date.now() } })),
  clear: (id) =>
    set((s) => {
      const next = { ...s.open };
      delete next[id];
      return { open: next };
    }),
}));

/** 409 invalid_state {reason: full, waitlist: {position, priority}} from POST /v1/drops/{id}/join. */
export function waitlistFromError(e: unknown): DropWaitlist | null {
  if (!(e instanceof ApiError) || e.details.reason !== 'full') return null;
  const w = e.details.waitlist as Partial<DropWaitlist> | undefined;
  if (!w || typeof w.position !== 'number') return null;
  return { position: w.position, priority: !!w.priority };
}

const QUIET = /^\/call\/|\/pod(\/|$)|^\/friends\/[^/]+\/chat|^\/match/;

/**
 * WS `drop.waitlist` → "A spot opened — join now". Seating is never automatic:
 * the person retries the join from the Drop page. Quiet during calls and pods.
 */
export function DropWaitlistNotifier() {
  const qc = useQueryClient();
  const { pathname } = useLocation();
  const path = useRef(pathname);
  path.current = pathname;
  useWsEvent('drop.waitlist', (d) => {
    if (!d?.drop_id || d.seat_available === false) return;
    useDropSeats.getState().mark(d.drop_id);
    void qc.invalidateQueries({ queryKey: qk.drop(d.drop_id) });
    void qc.invalidateQueries({ queryKey: qk.drops });
    if (QUIET.test(path.current)) return;
    const title = qc.getQueryData<Drop>(qk.drop(d.drop_id))?.title ?? qc.getQueryData<{ drops: Drop[] }>(qk.drops)?.drops.find((x) => x.id === d.drop_id)?.title;
    toast.info('A spot opened — join now', title ? `Open “${title}” to take your seat.` : 'Open the Drop to take your seat.');
  });
  return null;
}
